import { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Flame, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { moveStickerOrderItem, stickerMutationError, type StickerOrderItem } from '../stickerOrder';

export function StickerOrderDialog({
  open,
  title,
  items,
  onClose,
  onSave,
}: {
  open: boolean;
  title: string;
  items: StickerOrderItem[];
  onClose: () => void;
  onSave: (ids: string[]) => Promise<void>;
}) {
  const [draft, setDraft] = useState<StickerOrderItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const wasOpen = useRef(false);
  const originalIds = useRef<string[]>([]);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setDraft(items);
      originalIds.current = items.map((item) => item.id);
      setError(null);
    }
    wasOpen.current = open;
  }, [open, items]);
  const changed = draft.some((item, index) => item.id !== originalIds.current[index]);
  const move = (id: string, target: number) =>
    setDraft((current) => moveStickerOrderItem(current, id, target));
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave(draft.map((item) => item.id));
      onClose();
    } catch (reason) {
      setError(stickerMutationError(reason));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(next) => !next && !busy && onClose()}>
      <DialogContent className="max-w-2xl" closeDisabled={busy}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Drag items or use the arrows. Hidden items are included. Changes apply when you save;
            Cancel discards changes.
          </DialogDescription>
        </DialogHeader>
        <ol
          className="max-h-[55vh] space-y-2 overflow-y-auto rounded-md border p-2"
          aria-label="Draft order"
        >
          {draft.map((item, index) => (
            <li
              key={item.id}
              draggable={!busy}
              onDragStart={(event) => {
                setDragging(item.id);
                event.dataTransfer.setData('text/plain', item.id);
                event.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(event) => {
                if (!busy) event.preventDefault();
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (!busy) move(event.dataTransfer.getData('text/plain') || dragging || '', index);
                setDragging(null);
              }}
              onDragEnd={() => setDragging(null)}
              className={`flex items-center gap-2 rounded border p-2 ${dragging === item.id ? 'opacity-50' : ''}`}
            >
              <GripVertical
                className="size-4 shrink-0 cursor-grab text-muted-foreground"
                aria-hidden="true"
              />
              <span className="w-6 text-right text-xs text-muted-foreground">{index + 1}</span>
              {item.thumbnailUrl && (
                <img
                  src={item.thumbnailUrl}
                  alt=""
                  loading="lazy"
                  className="size-10 shrink-0 object-contain"
                />
              )}
              {!item.thumbnailUrl && item.id === 'trending' && (
                <Flame
                  className="size-10 shrink-0 p-2"
                  strokeWidth={1.75}
                  aria-label="Default Trending icon"
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{item.label}</p>
                <p className="truncate font-mono text-xs text-muted-foreground">
                  {item.id}
                  {item.status === 'DELISTED' ? ' · Hidden' : ''}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                aria-label={`Move ${item.label} up`}
                disabled={busy || index === 0}
                onClick={() => move(item.id, index - 1)}
              >
                <ArrowUp className="size-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                aria-label={`Move ${item.label} down`}
                disabled={busy || index === draft.length - 1}
                onClick={() => move(item.id, index + 1)}
              >
                <ArrowDown className="size-4" />
              </Button>
            </li>
          ))}
        </ol>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={busy || !changed} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
